import { useComprehensiveTopNav, useCustomToast, useInvoicePagination, useSearch, useWindowResize } from 'hooks';
import routes from 'lib/api/routes';
import { Button, DropdownButton, LoadingIndicator, Pagination, SearchBar, Typography } from 'lib/components';
import CustomRangedDatePickerModal from 'lib/components/CustomRangedDatePickerModal';
import { InvoicesDataTable } from 'lib/components/DataTables';
import { DropdownItem } from 'lib/components/types';
import { InvoicePaymentStatus as IPS, InvoicePaymentStatusType as ST, InvoiceStatusType, Timing } from 'lib/util';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import {
	actions,
	RequestManager,
	useAppDispatch,
	useSelectHospital,
	useSelectHospitals,
	useSelectInvoices,
	useSelectRequest,
} from 'store';
import { Invoice } from 'types';
import AccrueInvoiceModal from './AccrueInvoiceModal';
import classes from './index.module.scss';

/**
 * Dropdown items for invoice status filtering
 * Used in the status filter dropdown
 */
const STATUS_DROPDOWN_ITEMS: DropdownItem<number>[] = [
	{ label: 'Created', value: InvoiceStatusType.CREATED },
	{ label: 'Voided', value: InvoiceStatusType.VOIDED },
];

/**
 * Dropdown items for payment status filtering
 * Uses the InvoicePaymentStatus utility to get display text
 */
const PAYMENT_STATUS_DROPDOWN_ITEMS: DropdownItem<number>[] = [
	{ label: IPS.text(ST.PAID), value: ST.PAID },
	{ label: IPS.text(ST.UNPAID), value: ST.UNPAID },
	{ label: IPS.text(ST.REFUNDED), value: ST.REFUNDED },
];

/**
 * InvoicesScreen component
 * Main screen for displaying and managing invoices
 * Includes search, filtering, and accruing functionality
 *
 * @returns {JSX.Element} - Rendered component
 */
const InvoicesScreen = () => {
	// Custom hooks for notifications and responsive design
	const toast = useCustomToast();
	const { isSmallDevice, isSmallerDevice } = useWindowResize();

	// Redux state and actions
	const dispatch = useAppDispatch();
	const { active } = useSelectRequest();
	const invoices = useSelectInvoices();
	const hospitals = useSelectHospitals();
	const getHospitalById = useSelectHospital();

	// Modal visibility states
	const [showAccrueInvoiceModal, setShowAccrueInvoiceModal] = useState(false);
	const [showDateModal, setShowDateModal] = useState(false);

	// Date range for accruing invoices
	const [startDate, setStartDate] = useState<Date>(new Date());
	const [endDate, setEndDate] = useState<Date>(new Date());

	// Search and filter states
	const [query, setQuery] = useState('');
	const [hospitalDropdownItems, setHospitalDropdownItems] = useState<DropdownItem<number>[]>([]);
	const [selectedHospitalDropdownItem, setSelectedHospitalDropdownItem] = useState<number>(-1);
	const [selectedStatusDropdownItem, setSelectedStatusDropdownItem] = useState<number>(-1);
	const [selectedPaymentStatusDropdownItem, setSelectedPaymentStatusDropdownItem] = useState<number>(-1);
	const [selectedYear, setSelectedYear] = useState('');

	/**
	 * Fetch invoices and hospitals data on component mount
	 */
	useEffect(() => {
		dispatch(actions.fetchInvoices());
		dispatch(actions.fetchHospitals());
	}, []);

	/**
	 * Set up pagination for invoices list
	 * Returns data and handlers for paginated display
	 */
	const { isDataFetched, invoiceListing, hospitalListing, setData, paginatedData, paginationProps } =
		useInvoicePagination(invoices, hospitals);

	/**
	 * Set up search functionality for invoices
	 * Filters invoices by hospital name
	 * First callback is executed when query is empty, second when query has content
	 */
	useSearch(
		query,
		() => {
			// When query is empty, show all invoices
			setData(invoiceListing);
		},
		() => {
			// When query has content, filter invoices by hospital name
			const result = invoiceListing.filter(
				(invoice: Invoice) =>
					getHospitalById(invoice.hospitalId)?.name?.toLowerCase().search(query.toLowerCase()) !== -1
			);
			setData(result);
		}
	);

	/**
	 * Filter invoices by selected hospital
	 * Updates the displayed data when hospital filter changes
	 */
	useEffect(() => {
		// Skip if no hospital is selected or no invoices are available
		if (selectedHospitalDropdownItem === -1 || invoiceListing.length === 0) return;

		// Filter invoices by hospital ID
		const filtered = invoiceListing.filter((invoice) => invoice.hospitalId === selectedHospitalDropdownItem);
		setData(filtered);
	}, [selectedHospitalDropdownItem]);

	/**
	 * Filter invoices by selected status
	 * Updates the displayed data when status filter changes
	 */
	useEffect(() => {
		// Skip if no status is selected
		if (selectedStatusDropdownItem === -1) return;

		// Filter invoices by status
		const result = invoiceListing.filter((invoice) => invoice.status === selectedStatusDropdownItem);
		setData(result);
	}, [selectedStatusDropdownItem]);

	/**
	 * Filter invoices by selected payment status
	 * Updates the displayed data when payment status filter changes
	 */
	useEffect(() => {
		// Skip if no payment status is selected
		if (selectedPaymentStatusDropdownItem === -1) return;

		// Filter invoices by payment status
		const result = invoiceListing.filter((invoice) => invoice.status === selectedPaymentStatusDropdownItem);
		setData(result);
	}, [selectedPaymentStatusDropdownItem]);

	/**
	 * Filter invoices by selected year
	 * Updates the displayed data when year filter changes
	 */
	useEffect(() => {
		// Skip if no year is selected
		if (selectedYear === '') return;

		// Filter invoices by creation year
		const result = invoiceListing.filter((invoice) => String(moment(invoice.createdAt).year()) === selectedYear);
		setData(result);
	}, [selectedYear]);

	/**
	 * Monitor API requests for invoice accrual
	 * Shows toast notifications for success/failure
	 */
	useEffect(() => {
		const RM = new RequestManager(active);
		if (RM.idle()) return;

		if (RM.req(routes.INVOICE_GENERATE_ACCRUE, 'POST')) {
			if (RM.failed()) {
				toast(active.message, 'error');
			} else if (RM.succeeded()) {
				toast(active.message);
			}
		}
	}, [active]);

	/**
	 * Initialize hospital dropdown items when data is fetched
	 * Maps hospital data to dropdown format
	 */
	useEffect(() => {
		if (isDataFetched) {
			setHospitalDropdownItems(() => hospitalListing.map(({ id, name }) => ({ label: name, value: id })));
		}
	}, [isDataFetched]);

	/**
	 * Handle accrue invoice action
	 * Dispatches the generateAccruedPDFs action with hospital ID and date range
	 *
	 * @param {number} hospitalId - ID of the hospital to accrue invoices for
	 */
	const handleAccrueInvoice = (hospitalId: number) => {
		dispatch(
			actions.generateAccruedPDFs({
				hospitalId,
				rangeFrom: Timing.formatDate(startDate),
				rangeTo: Timing.formatDate(endDate),
			})
		);
	};

	/**
	 * Handle hospital dropdown selection
	 * Updates the selected hospital state
	 *
	 * @param {number} value - Selected hospital ID
	 */
	const handleHospitalDropdownItemSelect = (value: number) => {
		setSelectedHospitalDropdownItem(value);
	};

	/**
	 * Handle status dropdown selection
	 * Updates the selected status state
	 *
	 * @param {number} value - Selected status value
	 */
	const handleStatusDropdownItemSelect = (value: number) => {
		setSelectedStatusDropdownItem(value);
	};

	/**
	 * Handle payment status dropdown selection
	 * Updates the selected payment status state
	 *
	 * @param {number} value - Selected payment status value
	 */
	const handlePaymentStatusDropdownItemSelect = (value: number) => {
		setSelectedPaymentStatusDropdownItem(value);
	};

	/**
	 * Handle year dropdown selection
	 * Updates the selected year state
	 *
	 * @param {string} value - Selected year
	 */
	const handleYearDropdownItemSelect = React.useCallback(
		(value: string) => {
			setSelectedYear(value);
		},
		[setSelectedYear]
	);

	// Set up top navigation with title
	useComprehensiveTopNav('Invoices');

	// Show loading indicator until data is fetched
	if (!isDataFetched) return <LoadingIndicator />;

	return (
		<>
			{/* Modal for accruing invoices with selected date range and hospital */}
			<AccrueInvoiceModal
				startDate={startDate}
				endDate={endDate}
				setStartDate={setStartDate}
				setEndDate={setEndDate}
				isOpen={showAccrueInvoiceModal}
				onClose={() => setShowAccrueInvoiceModal(false)}
				onAccrueInvoice={handleAccrueInvoice}
			/>

			{/* Modal for selecting date range for accruing invoices */}
			<CustomRangedDatePickerModal
				onDatesSet={(start, end) => {
					setStartDate(start);
					setEndDate(end);
					setShowAccrueInvoiceModal(true);
				}}
				isOpen={showDateModal}
				onClose={() => setShowDateModal(false)}
			/>

			{/* Main container for invoices screen */}
			<div
				className={classes['invoices-container']}
				style={{
					padding: isSmallDevice ? '0 1rem' : undefined,
				}}
			>
				{/* Search and action row */}
				<div
					className={classes['row']}
					style={{
						justifyContent: 'space-between',
					}}
				>
					{/* Search bar for filtering invoices */}
					<SearchBar
						query={query}
						setQuery={setQuery}
						onSearch={() => console.log({ query })}
						className={classes['invoices-searchbar']}
						placeholder="Search..."
					/>
					{/* Action buttons */}
					<div
						className={classes['row']}
						style={{
							marginTop: '1.3rem',
						}}
					>
						<Button title="Accrue Invoices" variant="standard" onClick={() => setShowDateModal(true)} />
					</div>
				</div>

				{/* Filter dropdowns row */}
				<div
					className={classes['row']}
					style={{
						marginTop: '0.1rem',
						marginBottom: '1rem',
					}}
				>
					{/* Filter label */}
					<Typography
						variant={isSmallerDevice ? 'h5' : 'h6'}
						style={{
							margin: '1rem 1rem 0 0',
						}}
						weight="medium"
					>
						Filter by:
					</Typography>

					{/* Hospital filter dropdown */}
					<div style={{ marginTop: '1rem', marginRight: '0.5rem' }}>
						<DropdownButton
							title="Hospital"
							list={hospitalDropdownItems}
							onItemSelect={handleHospitalDropdownItemSelect}
						/>
					</div>

					{/* Status filter dropdown */}
					<div style={{ marginTop: '1rem', marginRight: '0.5rem' }}>
						<DropdownButton
							title="Status"
							list={STATUS_DROPDOWN_ITEMS}
							onItemSelect={handleStatusDropdownItemSelect}
						/>
					</div>

					{/* Payment status filter dropdown */}
					<div style={{ marginTop: '1rem', marginRight: '0.5rem' }}>
						<DropdownButton
							title="Payment Status"
							list={PAYMENT_STATUS_DROPDOWN_ITEMS}
							onItemSelect={handlePaymentStatusDropdownItemSelect}
						/>
					</div>

					{/* Year filter dropdown - dynamically generated from invoice dates */}
					<div style={{ marginTop: '1rem', marginRight: '0.5rem' }}>
						<DropdownButton
							title="Year"
							list={Array.from(new Set(invoiceListing.map((item) => moment(item.createdAt).year()))).map(
								(item) => ({ label: String(item), value: String(item) })
							)}
							onItemSelect={handleYearDropdownItemSelect}
						/>
					</div>
				</div>

				{/* Invoices data table */}
				<InvoicesDataTable invoices={paginatedData} />

				{/* Pagination controls */}
				<Pagination {...paginationProps} />
			</div>
		</>
	);
};

export default InvoicesScreen;
